'use client';

// @ts-expect-error - BlendVision player type definitions issue
import { c as _c } from "react/compiler-runtime";
import { PremiumPlayer } from '@blendvision/player/react';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';

// 🥷🏼 This is a workaround to avoid the type error
// 🥷🏼 don't use like this in your code
// 🥷🏼 (because playcraft/react is not installed in the project
// 🥷🏼 it's a dependency of the BlendVision player)
import { PremiumPlayerProps } from 'playcraft/react';
import { CSSProperties, RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'usehooks-ts';
import IconCode from '@/components/assets/icon_code.svg';
import IconCopy from '@/components/assets/icon_copy.svg';
import IconFB from '@/components/assets/icon_fb.svg';
import IconLink from '@/components/assets/icon_link.svg';
import IconTwitter from '@/components/assets/icon_twitter.svg';
import IconVideoShare from '@/components/assets/icon_video_share.svg';
import PopoverMenu from '@/components/PopoverMenu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Toaster } from '@/components/ui/sonner';
import ViverseButton from '@/components/ViverseButton';
import ViverseDialog from '@/components/ViverseDialog';
import ViverseTextarea from '@/components/ViverseTextarea';
import { useBlendVisionContext } from '@/context/BlendVisionContext';
import { useUAContext } from '@/context/UAContext';
import { useFullscreenStatus } from '@/hooks/useFullscreenStatus';
import { useShareLink } from '@/hooks/useShareLink';
import { createVideoEmbedCode } from '@/lib/embed';
import { showToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { DeviceType } from '@/types/configs';
type Translations = {
  shareCopyLinkCopied: string;
  shareTwitter: string;
  shareFacebook: string;
  shareCopyLink: string;
  embedTitle: string;
  embedButton: string;
  embedCopiedToast: string;
};
type EmbedDialogProps = {
  isDialogOpen: boolean;
  shareUrl?: string;
  videoTitle?: string;
  portalContainer?: HTMLElement | null;
  translations: Translations;
  handleOpenChange: (open: boolean) => void;
  handleClose: () => void;
};

// 🥷🏼 Use arbitrary values (e.g., gap-[16px], size-[24px]) because Tailwind tokens don't work in fullscreen portal

function EmbedDialog(t0) {
  const $ = _c(26);
  const {
    isDialogOpen,
    shareUrl,
    videoTitle,
    portalContainer,
    translations,
    handleOpenChange,
    handleClose
  } = t0;
  const [embedCode, setEmbedCode] = useState("");
  let t1;
  let t2;
  if ($[0] !== isDialogOpen || $[1] !== shareUrl || $[2] !== videoTitle) {
    t1 = () => {
      if (isDialogOpen && shareUrl) {
        const embedCodeValue = createVideoEmbedCode(shareUrl, videoTitle);
        setEmbedCode(embedCodeValue);
      }
    };
    t2 = [isDialogOpen, shareUrl, videoTitle];
    $[0] = isDialogOpen;
    $[1] = shareUrl;
    $[2] = videoTitle;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== embedCode || $[6] !== handleClose || $[7] !== translations.embedCopiedToast) {
    t3 = () => {
      navigator.clipboard.writeText(embedCode);
      showToast({
        message: translations.embedCopiedToast,
        type: "success"
      });
      handleClose();
    };
    $[5] = embedCode;
    $[6] = handleClose;
    $[7] = translations.embedCopiedToast;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleEmbedCodeCopy = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => setEmbedCode(e.target.value);
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== embedCode) {
    t5 = <div className="flex flex-col gap-[16px]"><ViverseTextarea className="bg-vive-primary-70 max-h-[340px] w-full border-transparent text-base" showMaxLength={false} value={embedCode} onChange={t4} /></div>;
    $[10] = embedCode;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <IconCopy className="mr-[8px] size-[24px]" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== handleEmbedCodeCopy || $[14] !== translations.embedButton) {
    t7 = <ViverseButton className="m-auto md:min-h-[48px] md:text-[18px]" onClick={handleEmbedCodeCopy}>{t6}{translations.embedButton}</ViverseButton>;
    $[13] = handleEmbedCodeCopy;
    $[14] = translations.embedButton;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <div className="flex w-full flex-1 flex-col gap-[32px] px-[40px] pb-[32px]">{t5}{t7}</div>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== handleClose || $[20] !== handleOpenChange || $[21] !== isDialogOpen || $[22] !== portalContainer || $[23] !== t8 || $[24] !== translations.embedTitle) {
    t9 = <ViverseDialog contentClassName="flex flex-col min-h-[216px] w-full md:max-w-[640px]" triggerClassName="contents" titleClassName="border-0 md:text-[24px]" title={translations.embedTitle} portalContainer={portalContainer} open={isDialogOpen} onClose={handleClose} onOpenChange={handleOpenChange}>{t8}</ViverseDialog>;
    $[19] = handleClose;
    $[20] = handleOpenChange;
    $[21] = isDialogOpen;
    $[22] = portalContainer;
    $[23] = t8;
    $[24] = translations.embedTitle;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
type ShareButtonProps = {
  translations: Translations;
  deviceType: DeviceType;
  shareLink: ReturnType<typeof useShareLink>;
  shareUrl?: string;
  videoTitle?: string;
  portalContainer?: HTMLElement;
};
function ShareButton(t0) {
  const $ = _c(62);
  const {
    translations,
    deviceType,
    shareLink,
    shareUrl,
    videoTitle,
    portalContainer
  } = t0;
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)") || deviceType === "desktop";
  const [isEmbedDialogOpen, setIsEmbedDialogOpen] = useState(false);
  const {
    isFullscreen
  } = useFullscreenStatus();
  let t1;
  if ($[0] !== isDesktop) {
    t1 = () => {
      if (isDesktop) {
        setOpen(true);
      }
    };
    $[0] = isDesktop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleMouseEnter = t1;
  let t2;
  if ($[2] !== isDesktop) {
    t2 = () => {
      if (isDesktop) {
        setOpen(false);
      }
    };
    $[2] = isDesktop;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleMouseLeave = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(_temp);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleOpen = t3;
  let t4;
  if ($[5] !== shareUrl || $[6] !== translations.shareCopyLinkCopied) {
    t4 = () => {
      navigator.clipboard.writeText(shareUrl);
      showToast({
        message: translations.shareCopyLinkCopied,
        type: "success"
      });
      setOpen(false);
    };
    $[5] = shareUrl;
    $[6] = translations.shareCopyLinkCopied;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleCopyLink = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PopoverTrigger asChild={true}><button type="button" onClick={handleOpen} className="1200:px-[7.5px] flex cursor-pointer items-center justify-center px-[6.75px] py-[4px] transition-opacity" aria-label="Share"><IconVideoShare className="1200:size-[30px] size-[27px] fill-white" /></button></PopoverTrigger>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== shareLink || $[10] !== videoTitle) {
    t6 = shareLink.createSharePlatformLink("twitter", videoTitle);
    $[9] = shareLink;
    $[10] = videoTitle;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IconTwitter className="fill-vive-primary-20 size-[16px]" />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== translations.shareTwitter) {
    t8 = <PopoverMenu.Item className="rounded-none">{t7}<div className="body-1 text-inherit">{translations.shareTwitter}</div></PopoverMenu.Item>;
    $[13] = translations.shareTwitter;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6 || $[16] !== t8) {
    t9 = <Link href={t6} target="_blank">{t8}</Link>;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== shareLink) {
    t10 = shareLink.createSharePlatformLink("facebook");
    $[18] = shareLink;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <IconFB className="fill-vive-primary-20 size-[16px]" />;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== translations.shareFacebook) {
    t12 = <PopoverMenu.Item className="rounded-none">{t11}<div className="body-1 text-inherit">{translations.shareFacebook}</div></PopoverMenu.Item>;
    $[21] = translations.shareFacebook;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t12) {
    t13 = <Link href={t10} target="_blank">{t12}</Link>;
    $[23] = t10;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <IconLink className="fill-vive-primary-20 size-[16px]" />;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== translations.shareCopyLink) {
    t15 = <div className="body-1 text-inherit">{translations.shareCopyLink}</div>;
    $[27] = translations.shareCopyLink;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== handleCopyLink || $[30] !== t15) {
    t16 = <PopoverMenu.Item className="rounded-none" onClick={handleCopyLink}>{t14}{t15}</PopoverMenu.Item>;
    $[29] = handleCopyLink;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setIsEmbedDialogOpen(true);
    t18 = <IconCode className="fill-vive-primary-20 size-[16px]" />;
    $[32] = t17;
    $[33] = t18;
  } else {
    t17 = $[32];
    t18 = $[33];
  }
  let t19;
  if ($[34] !== translations.embedTitle) {
    t19 = <PopoverMenu.Item className="rounded-none" onClick={t17}>{t18}<div className="body-1 text-inherit">{translations.embedTitle}</div></PopoverMenu.Item>;
    $[34] = translations.embedTitle;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t13 || $[37] !== t16 || $[38] !== t19 || $[39] !== t9) {
    t20 = <PopoverMenu className="bg-vive-primary-70/80 p-0 lg:p-[8px]"><PopoverMenu.Group>{t9}{t13}{t16}{t19}</PopoverMenu.Group></PopoverMenu>;
    $[36] = t13;
    $[37] = t16;
    $[38] = t19;
    $[39] = t9;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== portalContainer || $[42] !== t20) {
    t21 = <PopoverContent id="share-popover-content" side="bottom" align="end" sideOffset={0} className="z-top border-none bg-transparent p-0" portalContainer={portalContainer}>{t20}</PopoverContent>;
    $[41] = portalContainer;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== open || $[45] !== t21) {
    t22 = <Popover open={open} onOpenChange={setOpen}>{t5}{t21}</Popover>;
    $[44] = open;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== handleMouseEnter || $[48] !== handleMouseLeave || $[49] !== t22) {
    t23 = <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="absolute top-[8px] right-[8px] md:top-[18px] md:right-[18px]">{t22}</div>;
    $[47] = handleMouseEnter;
    $[48] = handleMouseLeave;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  const t24 = isFullscreen ? portalContainer : null;
  let t25;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setIsEmbedDialogOpen(false);
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== isEmbedDialogOpen || $[53] !== shareUrl || $[54] !== t24 || $[55] !== translations || $[56] !== videoTitle) {
    t26 = <EmbedDialog isDialogOpen={isEmbedDialogOpen} shareUrl={shareUrl} videoTitle={videoTitle} translations={translations} portalContainer={t24} handleOpenChange={setIsEmbedDialogOpen} handleClose={t25} />;
    $[52] = isEmbedDialogOpen;
    $[53] = shareUrl;
    $[54] = t24;
    $[55] = translations;
    $[56] = videoTitle;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Toaster toastOptions={{
      className: "hidden [:fullscreen_&]:block"
    }} />;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t23 || $[60] !== t26) {
    t28 = <>{t23}{t26}{t27}</>;
    $[59] = t23;
    $[60] = t26;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  return t28;
}
function _temp(prev) {
  return !prev;
}
const applySimpleSliderStyles = (simpleSlider: HTMLElement | null) => {
  if (!simpleSlider) return;
  simpleSlider.style.backgroundColor = 'var(--color-vive-primary-70)';
  simpleSlider.style.borderColor = 'var(--color-vive-primary-70)';
  const firstChild = simpleSlider.children[0] as HTMLElement | null;
  if (firstChild) {
    firstChild.style.backgroundColor = 'var(--color-vive-brand-50)';
    firstChild.style.opacity = '0.3';
  }
  const secondChild = simpleSlider.children[1] as HTMLElement | null;
  if (secondChild) {
    secondChild.style.backgroundColor = 'var(--color-vive-brand-50)';
  }
  const thirdChild = simpleSlider.children[2] as HTMLElement | null;
  if (thirdChild) {
    thirdChild.style.backgroundColor = 'var(--color-vive-brand-50)';
  }
};
type ShareButtonMount = {
  buttonContainer: HTMLElement;
  portalContainer: HTMLElement;
};
const useInsertShareButton = (containerRef, shareUrl) => {
  const $ = _c(5);
  const [shareButtonMount, setShareButtonMount] = useState(null);
  const {
    deviceType
  } = useUAContext();
  const isDesktop = useMediaQuery("(min-width: 1024px)") || deviceType === "desktop";
  let t0;
  let t1;
  if ($[0] !== containerRef || $[1] !== isDesktop || $[2] !== shareUrl) {
    t0 = () => {
      const container = containerRef.current;
      if (!container) {
        setShareButtonMount(null);
        return;
      }
      const videoOverlayBackdrop = container.querySelector("[class*=\"overlay-backdrop\"]") as HTMLElement | null;
      const videoContainer = videoOverlayBackdrop?.parentElement as HTMLElement | null;
      const volumnContainer = videoOverlayBackdrop?.previousElementSibling as HTMLElement | null;
      const volumeControl = isDesktop ? volumnContainer?.previousElementSibling?.children[4] : volumnContainer?.children[3];
      if (!volumeControl || !videoOverlayBackdrop || !videoContainer) {
        setShareButtonMount(null);
        return;
      }
      const simpleSlider = volumeControl?.children[1] as HTMLElement | null;
      applySimpleSliderStyles(simpleSlider);
      if (!shareUrl) {
        setShareButtonMount(null);
        return;
      }
      const buttonContainer = document.createElement("div");
      videoContainer.appendChild(buttonContainer);
      setShareButtonMount({
        buttonContainer,
        portalContainer: videoContainer
      });
      return () => {
        buttonContainer.remove();
      };
    };
    t1 = [containerRef, shareUrl, isDesktop];
    $[0] = containerRef;
    $[1] = isDesktop;
    $[2] = shareUrl;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return shareButtonMount;
};
type ViverseVideoPlayerProps = Omit<PremiumPlayerProps, 'validationHost' | 'license'> & {
  containerClassName?: string;
  share?: {
    url: string;
  };
  autoPlay?: boolean;
  hideUI?: boolean;
  poster?: string;
};
export default function ViverseVideoPlayer(t0) {
  const $ = _c(57);
  let autoPlay;
  let containerClassName;
  let hideUI;
  let props;
  let share;
  if ($[0] !== t0) {
    ({
      share,
      containerClassName,
      autoPlay,
      hideUI,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = autoPlay;
    $[2] = containerClassName;
    $[3] = hideUI;
    $[4] = props;
    $[5] = share;
  } else {
    autoPlay = $[1];
    containerClassName = $[2];
    hideUI = $[3];
    props = $[4];
    share = $[5];
  }
  const t = useTranslations();
  const locale = useLocale();
  const videoRef = useRef(null);
  const playerContainerRef = useRef(null);
  const {
    sdkLicenseKey
  } = useBlendVisionContext();
  const {
    deviceType
  } = useUAContext();
  const shareUrl = hideUI ? undefined : share?.url;
  const shareLink = useShareLink(shareUrl);
  const shareButtonMount = useInsertShareButton(playerContainerRef, shareUrl);
  let shareButton = null;
  if (shareUrl && shareButtonMount) {
    const t1 = props.title;
    const t2 = shareButtonMount.portalContainer;
    let t3;
    if ($[6] !== t) {
      t3 = t("profile-page.share-twitter");
      $[6] = t;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t) {
      t4 = t("profile-page.share-facebook");
      $[8] = t;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t) {
      t5 = t("profile-page.share-copy-link");
      $[10] = t;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== t) {
      t6 = t("profile-page.share-link-copied");
      $[12] = t;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t) {
      t7 = t("embed-modal.title");
      $[14] = t;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== t) {
      t8 = t("embed-modal.button");
      $[16] = t;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== t) {
      t9 = t("embed-copied-toast.title");
      $[18] = t;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    let t10;
    if ($[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
      t10 = {
        shareTwitter: t3,
        shareFacebook: t4,
        shareCopyLink: t5,
        shareCopyLinkCopied: t6,
        embedTitle: t7,
        embedButton: t8,
        embedCopiedToast: t9
      };
      $[20] = t3;
      $[21] = t4;
      $[22] = t5;
      $[23] = t6;
      $[24] = t7;
      $[25] = t8;
      $[26] = t9;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== deviceType || $[29] !== props.title || $[30] !== shareButtonMount.portalContainer || $[31] !== shareLink || $[32] !== shareUrl || $[33] !== t10) {
      t11 = <ShareButton videoTitle={t1} deviceType={deviceType} shareLink={shareLink} shareUrl={shareUrl} portalContainer={t2} translations={t10} />;
      $[28] = deviceType;
      $[29] = props.title;
      $[30] = shareButtonMount.portalContainer;
      $[31] = shareLink;
      $[32] = shareUrl;
      $[33] = t10;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    let t12;
    if ($[35] !== shareButtonMount.buttonContainer || $[36] !== t11) {
      t12 = createPortal(t11, shareButtonMount.buttonContainer);
      $[35] = shareButtonMount.buttonContainer;
      $[36] = t11;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    shareButton = t12;
  }
  const t1 = hideUI && "**:[[class*=\"overlay\"]]:hidden";
  let t2;
  if ($[38] !== containerClassName || $[39] !== t1) {
    t2 = cn("size-full", containerClassName, t1);
    $[38] = containerClassName;
    $[39] = t1;
    $[40] = t2;
  } else {
    t2 = $[40];
  }
  const t3 = autoPlay || props.muted;
  let t4;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      "--theme-color": "var(--color-vive-brand-50)",
      ".kks-player__seek-bar > div > div > div:nth-of-type(3)": {
        backgroundColor: "var(--color-vive-brand-60)"
      },
      ".kks-player__seek-bar > div > div:nth-of-type(2)": {
        backgroundColor: "var(--color-vive-brand-50)"
      }
    };
    $[41] = t4;
  } else {
    t4 = $[41];
  }
  let t5;
  if ($[42] !== locale) {
    let t6;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = /-.+$/;
      $[44] = t6;
    } else {
      t6 = $[44];
    }
    t5 = locale.replace(t6, "");
    $[42] = locale;
    $[43] = t5;
  } else {
    t5 = $[43];
  }
  let t6;
  if ($[45] !== t5) {
    t6 = {
      locale: t5
    };
    $[45] = t5;
    $[46] = t6;
  } else {
    t6 = $[46];
  }
  let t7;
  if ($[47] !== autoPlay || $[48] !== props || $[49] !== sdkLicenseKey || $[50] !== t3 || $[51] !== t6) {
    t7 = <PremiumPlayer {...props} autoPlay={autoPlay} muted={t3} license={sdkLicenseKey} videoRef={videoRef} style={t4 as CSSProperties} intl={t6} />;
    $[47] = autoPlay;
    $[48] = props;
    $[49] = sdkLicenseKey;
    $[50] = t3;
    $[51] = t6;
    $[52] = t7;
  } else {
    t7 = $[52];
  }
  let t8;
  if ($[53] !== shareButton || $[54] !== t2 || $[55] !== t7) {
    t8 = <div ref={playerContainerRef} className={t2}>{t7}{shareButton}</div>;
    $[53] = shareButton;
    $[54] = t2;
    $[55] = t7;
    $[56] = t8;
  } else {
    t8 = $[56];
  }
  return t8;
}